/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useRef, useState } from "react";
import { Link } from "../components/ui/Link";
import { Send } from "lucide-react";
import { queueManualRun, searchProviders } from "../api/providers";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button";
import { DateTimePicker } from "../components/ui/DateTimePicker";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { convertTo24Hour, getSmartStartTime, type TimeValue } from "../lib/date-utils";

const SLOT_LENGTHS = [
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "50", label: "50 minutes" },
  { value: "60", label: "60 minutes" },
];

/**
 * The agent normally wakes when a cancellation frees a slot; this surface lets an admin wake it
 * for a standing open slot instead. The run it queues is the ordinary pipeline end to end, so it
 * starts real outreach; hence the admin gate and the confirmation step.
 */
export function ManualRun() {
  const { hasRole } = useAuth();
  useChrome({}, []);

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Run the agent
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          Point the scheduling agent at a provider&apos;s open slot. It ranks that state&apos;s
          waitlist pool and texts the best-matched families, exactly as if a cancellation had freed
          the slot.
        </p>
      </header>
      {hasRole("Admin") ? (
        <ManualRunForm />
      ) : (
        <p className="enter text-sm text-text-tertiary">
          Starting a run texts real families, so this action is reserved for admins.
        </p>
      )}
    </div>
  );
}

function ManualRunForm() {
  const { showToast } = useToast();
  const [providerId, setProviderId] = useState("");
  const providerLabels = useRef<Record<string, string>>({});
  const [date, setDate] = useState<Date | null>(tomorrow);
  const [time, setTime] = useState<TimeValue | null>(() => getSmartStartTime(15));
  const [slotLength, setSlotLength] = useState("50");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(async (search: string) => {
    const result = await searchProviders(search);
    if (!result.ok) {
      return [];
    }
    return result.value.map((provider) => {
      const name = [provider.firstName, provider.lastName]
        .filter((part) => part !== null)
        .join(" ");
      const label =
        provider.licensedStates.length > 0
          ? `${name} (${provider.licensedStates.join(", ")})`
          : name;
      providerLabels.current[provider.id] = label;
      return { value: provider.id, label };
    });
  }, []);

  const start = slotStart(date, time);
  const ready = providerId !== "" && start !== null && start > new Date();

  async function submit() {
    if (start === null) {
      return;
    }
    setBusy(true);
    setError(null);
    const end = new Date(start.getTime() + Number(slotLength) * 60_000);
    const result = await queueManualRun({
      providerId,
      slotStartsAt: start.toISOString(),
      slotEndsAt: end.toISOString(),
    });
    setBusy(false);
    setConfirming(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    setQueued(true);
    showToast("success", "Run queued. The agent picks it up within a minute.");
  }

  if (queued) {
    return (
      <div className="border border-border bg-bg-elevated shadow-card enter max-w-xl space-y-4 rounded-3xl p-6" role="status">
        <p className="text-sm leading-relaxed text-text-secondary">
          Run queued. The agent picks it up within a minute and it appears in Runs as
          &ldquo;Started by staff&rdquo;.
        </p>
        <div className="flex items-center gap-3">
          <Link to="/agents/runs">
            <Button variant="primary" size="sm">
              Watch it in Runs
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQueued(false);
              setDate(tomorrow());
              setTime(getSmartStartTime(15));
            }}
          >
            Queue another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="enter space-y-5">
      <ErrorMessage message={error} className="mb-0" />

      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-end xl:flex xl:flex-row xl:items-end">
          <Select
            label="Provider"
            className="w-full xl:w-80"
            value={providerId}
            onChange={setProviderId}
            fetchOptions={fetchProviders}
            searchable
            placeholder="Find a provider by name…"
          />
          <DateTimePicker
            label="Slot start"
            className="xl:w-96"
            dateValue={date}
            timeValue={time}
            onDateChange={(next) => {
              setDate(next);
              if (next !== null && time === null) {
                setTime(getSmartStartTime(15));
              }
            }}
            onTimeChange={setTime}
            minDate={new Date()}
            timeIncrement={15}
          />
        </div>
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:items-end xl:flex xl:flex-row xl:items-end">
          <Select
            label="Slot length"
            className="w-full sm:w-56 xl:w-44"
            value={slotLength}
            onChange={setSlotLength}
            options={SLOT_LENGTHS}
          />
          <div>
            <Button
              variant="primary"
              disabled={!ready || busy}
              onClick={() => setConfirming(true)}
            >
              <Send size={15} />
              Run the agent
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        title="Start real outreach?"
        size="sm"
        asSheet={false}
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            The agent will rank the pool for{" "}
            {providerLabels.current[providerId] ?? "the selected provider"} and text the
            best-matched families about the {slotLength}-minute slot starting{" "}
            {start !== null
              ? start.toLocaleString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
              : ""}
            . These are real text messages to real families.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void submit()}>
              {busy ? (
                <>
                  <Spinner size="xs" className="text-bg-base" />
                  Queueing…
                </>
              ) : (
                "Start run"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function tomorrow(): Date {
  const next = new Date();
  next.setDate(next.getDate() + 1);
  next.setHours(0, 0, 0, 0);
  return next;
}

function slotStart(date: Date | null, time: TimeValue | null): Date | null {
  if (date === null || time === null) {
    return null;
  }
  const start = new Date(date);
  start.setHours(convertTo24Hour(time.hours, time.period), time.minutes, 0, 0);
  return start;
}
